// src/components/profile/business-profile/BusinessProfile.tsx
import React, { useEffect } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import LanguageIcon from "@mui/icons-material/Language";
import StorefrontIcon from "@mui/icons-material/Storefront";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import { ProfileData } from "../types/profile";
import ProfileAvatar from "./ProfileAvatar";
import CommonButtons from "../../../components/CommonButtons";
import { useImageUpload } from "../hooks/useImageUpload";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { getUser } from "src/shared/services/services";

interface BusinessProfileProps {
  profileData: ProfileData;
  businessType: string;
  onEdit: () => void;
}

const BusinessProfile: React.FC<BusinessProfileProps> = ({ profileData, businessType, onEdit }) => {
  const { uploadImage } = useImageUpload();
  const { user, setUser } = useUser();

  const handleImageUpload = async (file: File) => {
    uploadImage(file, `/pos/image/${profileData._id.$oid}`, "put", "business_logo.png", `Logo${profileData.organization_name}`)
      .then((res) => {
        window.location.reload();
      })
      .catch((error) => console.error(error));
  };

  const leftColumnDetails = [
    { icon: <PersonIcon className="text-blue-500 text-sm" />, label: "User Name", value: profileData.user_name },
    { icon: <PersonIcon className="text-green-500 text-sm" />, label: "Name", value: profileData.name },
    { icon: <BusinessIcon className="text-indigo-500 text-sm" />, label: "Organization Name", value: profileData.organization_name },
    { icon: <BusinessIcon className="text-indigo-400 text-sm" />, label: "Org. Arabic Name", value: profileData.org_arabic_name },
    { icon: <StorefrontIcon className="text-orange-500 text-sm" />, label: "Business Type", value: businessType },
    { icon: <EmailIcon className="text-red-500 text-sm" />, label: "Email ID", value: profileData.email_id },
    { icon: <CalendarTodayIcon className="text-purple-500 text-sm" />, label: "Date of Birth", value: profileData.date_of_birth },
    { icon: <PhoneIcon className="text-yellow-500 text-sm" />, label: "Phone Number", value: profileData.phone_number },
  ];

  const rightColumnDetails = [
    { icon: <LocationOnIcon className="text-red-500 text-sm" />, label: "Home Branch Address", value: profileData.address_line_1 },
    { icon: <LocationOnIcon className="text-red-400 text-sm" />, label: "Business Location", value: profileData.business_location },
    { icon: <AccessTimeIcon className="text-blue-500 text-sm" />, label: "Time Zone", value: profileData.time_zone },
    { icon: <BusinessIcon className="text-gray-600 text-sm" />, label: "Country", value: profileData.country },
    { icon: <BusinessIcon className="text-gray-600 text-sm" />, label: "State", value: profileData.state },
    { icon: <LanguageIcon className="text-teal-500 text-sm" />, label: "Postal Code", value: profileData.postal_code },
  ];

  return (
    <div className="bg-gray-50">
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Compact Header */}
          <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-2">
            <div className="flex items-center gap-4">
              <ProfileAvatar image={String(`${user?.image_url}`)} onImageChange={handleImageUpload} tooltipText="Change Logo" size="w-14 h-14" className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate hover:text-clip">{profileData.organization_name || "Organization Name"}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{businessType || "Business Type"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Two Column Layout */}
          <div className="p-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
              {/* Left Column */}
              <div className="space-y-3">
                {leftColumnDetails.map((detail, index) => (
                  <div key={index} className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-1 rounded-md bg-gray-50">{detail.icon}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{detail.label}</p>
                      <p className="text-sm text-gray-900 break-words">{detail.value || <span className="text-gray-400 italic text-xs">Not provided</span>}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Column */}
              <div className="space-y-3">
                {rightColumnDetails.map((detail, index) => (
                  <div key={index} className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="p-1 rounded-md bg-gray-50">{detail.icon}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{detail.label}</p>
                      <p className="text-sm text-gray-900 break-words">{detail.value || <span className="text-gray-400 italic text-xs">Not provided</span>}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compact Button */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex justify-end">
              <CommonButtons
                label="Edit Business Profile"
                onClick={onEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg shadow-sm hover:shadow-md transition-all duration-200 font-medium"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;